import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import config from '../config';
import { useIsMobile } from '../hooks/useMediaQuery';
import { getIdToken } from '../attendedUtils';

const ENTITY_TYPES = [
    { value: 'artist',   label: 'Artist',   endpoint: '/artists' },
    { value: 'venue',    label: 'Venue',     endpoint: '/venues' },
    { value: 'promoter', label: 'Promoter',  endpoint: '/promoters' },
    { value: 'member',   label: 'Member',    endpoint: '/members' },
];

const METRICS = ['Show History'];

function formatDateForName(dateStr) {
    if (!dateStr) return '';
    const [y, m, d] = dateStr.split('-');
    return `${m}/${d}/${y}`;
}

function AddProject() {
    const isMobile = useIsMobile();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [dupWarning, setDupWarning] = useState(null);

    const [form, setForm] = useState({
        entity_type: 'artist',
        entity_id: '',
        metric: 'Show History',
        date_start: '',
        date_end: '',
        estimated_value: '',
    });

    // Entity search
    const [entities, setEntities] = useState([]);
    const [entitiesLoading, setEntitiesLoading] = useState(false);
    const [entitySearch, setEntitySearch] = useState('');
    const [selectedEntity, setSelectedEntity] = useState(null);

    // Load entities when entity_type changes
    useEffect(() => {
        const obj = ENTITY_TYPES.find(t => t.value === form.entity_type);
        if (!obj) return;
        setEntitiesLoading(true);
        setSelectedEntity(null);
        setForm(prev => ({ ...prev, entity_id: '' }));
        setEntitySearch('');
        axios.get(`${config.API_URL}${obj.endpoint}`)
            .then(res => {
                const data = res.data;
                const list = Array.isArray(data) ? data : (data[`${obj.value}s`] || data.members || data.promoters || data.festivals || []);
                setEntities(list);
                setEntitiesLoading(false);
            })
            .catch(() => {
                setEntities([]);
                setEntitiesLoading(false);
            });
    }, [form.entity_type]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError(null);
        setDupWarning(null);
    };

    const handleEntityTypeChange = (e) => {
        setForm(prev => ({ ...prev, entity_type: e.target.value, entity_id: '' }));
        setSelectedEntity(null);
        setEntitySearch('');
    };

    const handleSelectEntity = (entity) => {
        setSelectedEntity(entity);
        setForm(prev => ({ ...prev, entity_id: entity.id }));
        setEntitySearch(entity.name);
    };

    const filteredEntities = entitySearch.trim()
        ? entities.filter(e => e.name.toLowerCase().includes(entitySearch.toLowerCase()))
        : entities.slice(0, 50);

    // Live project name preview
    const previewName = () => {
        const entityName = selectedEntity ? selectedEntity.name : '';
        if (!entityName || !form.metric || !form.date_start || !form.date_end) return null;
        return `The ${entityName} Project - ${form.metric} - ${formatDateForName(form.date_start)} to ${formatDateForName(form.date_end)}`;
    };

    const today = new Date().toISOString().split('T')[0];

    const handleSubmit = async () => {
        if (!form.entity_id) { setError('Please select an entity.'); return; }
        if (!form.date_start) { setError('Start date is required.'); return; }
        if (!form.date_end) { setError('End date is required.'); return; }
        if (form.date_end > today) { setError('End date cannot be in the future.'); return; }
        if (form.date_start > form.date_end) { setError('Start date must be before end date.'); return; }
        if (!form.estimated_value || parseInt(form.estimated_value) < 1) { setError('Estimated value must be at least 1.'); return; }

        setSaving(true);
        setError(null);
        setDupWarning(null);
        try {
            const token = await getIdToken();
            const res = await axios.post(`${config.API_URL}/projects`, {
                entity_type: form.entity_type,
                entity_id: parseInt(form.entity_id),
                metric: form.metric,
                date_start: form.date_start,
                date_end: form.date_end,
                estimated_value: parseInt(form.estimated_value),
            }, { headers: { Authorization: token } });
            navigate(`/projects/${res.data.id}`);
        } catch (err) {
            if (err.response?.status === 409 && err.response.data?.error === 'duplicate') {
                setDupWarning(err.response.data.existing);
            } else {
                setError(err.response?.data?.error || 'Failed to save project.');
            }
            setSaving(false);
        }
    };

    const [dropdownOpen, setDropdownOpen] = useState(false);

    return (
        <div style={styles.container}>
            <Link to="/projects" style={styles.backLink}>← Back to Projects</Link>
            <h1 style={styles.title}>Add Project</h1>

            {error && <div style={styles.errorBox}>{error}</div>}
            {dupWarning && (
                <div style={styles.dupBox}>
                    <strong>⚠ A project with this exact scope already exists:</strong>
                    <div style={{ marginTop: '8px' }}>{dupWarning.name}</div>
                    <div style={{ marginTop: '10px' }}>
                        <Link to={`/projects/${dupWarning.id}`} style={styles.dupLink}>→ View existing project</Link>
                    </div>
                </div>
            )}

            <div style={styles.form}>

                <div style={styles.sectionHeader}>Entity</div>

                <div style={styles.field}>
                    <label style={styles.label}>Entity Type *</label>
                    <select name="entity_type" value={form.entity_type} onChange={handleEntityTypeChange} style={styles.input}>
                        {ENTITY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Entity *</label>
                    <div style={{ position: 'relative' }}>
                        <input
                            type="text"
                            placeholder={entitiesLoading ? 'Loading...' : `Search for a ${form.entity_type}...`}
                            value={entitySearch}
                            onChange={e => { setEntitySearch(e.target.value); setDropdownOpen(true); setSelectedEntity(null); setForm(prev => ({ ...prev, entity_id: '' })); }}
                            onFocus={() => setDropdownOpen(true)}
                            onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
                            style={styles.input}
                            disabled={entitiesLoading}
                        />
                        {dropdownOpen && entitySearch && filteredEntities.length > 0 && (
                            <div style={styles.dropdown}>
                                {filteredEntities.map(e => (
                                    <div
                                        key={e.id}
                                        style={styles.dropdownItem}
                                        onMouseDown={() => handleSelectEntity(e)}
                                    >
                                        {e.name}
                                        {(e.city || e.state) && <span style={{ color: '#666', marginLeft: '8px', fontSize: '12px' }}>{[e.city, e.state].filter(Boolean).join(', ')}</span>}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                    {selectedEntity && (
                        <div style={styles.selectedBadge}>✓ Selected: <strong>{selectedEntity.name}</strong></div>
                    )}
                </div>

                <div style={styles.sectionDivider} />
                <div style={styles.sectionHeader}>Project Scope</div>

                <div style={styles.field}>
                    <label style={styles.label}>Metric *</label>
                    <select name="metric" value={form.metric} onChange={handleChange} style={styles.input}>
                        {METRICS.map(m => <option key={m} value={m}>{m}</option>)}
                    </select>
                </div>

                <div style={isMobile ? { ...styles.twoCol, ...Smobile.twoCol } : styles.twoCol}>
                    <div style={styles.field}>
                        <label style={styles.label}>Start Date *</label>
                        <input type="date" name="date_start" value={form.date_start} onChange={handleChange} max={today} style={styles.input} />
                    </div>
                    <div style={styles.field}>
                        <label style={styles.label}>End Date * (cannot be in the future)</label>
                        <input type="date" name="date_end" value={form.date_end} onChange={handleChange} max={today} style={styles.input} />
                    </div>
                </div>

                <div style={styles.field}>
                    <label style={styles.label}>Estimated Value * (number of shows you expect to log)</label>
                    <input
                        type="number"
                        name="estimated_value"
                        value={form.estimated_value}
                        onChange={handleChange}
                        min="1"
                        placeholder="e.g. 200"
                        style={styles.input}
                    />
                </div>

                {previewName() && (
                    <>
                        <div style={styles.sectionDivider} />
                        <div style={styles.sectionHeader}>Project Name Preview</div>
                        <div style={styles.namePreview}>{previewName()}</div>
                    </>
                )}

                <div style={{ ...styles.sectionDivider, marginBottom: '24px' }} />
                <button onClick={handleSubmit} disabled={saving} style={styles.submitButton}>
                    {saving ? 'Saving...' : 'Create Project'}
                </button>
            </div>
        </div>
    );
}

const styles = {
    container: { maxWidth: '680px', margin: '0 auto' },
    backLink: { color: '#bb86fc', textDecoration: 'none', fontSize: '14px', display: 'inline-block', marginBottom: '24px' },
    title: { fontSize: '32px', fontWeight: 'bold', color: '#e0e0e0', marginBottom: '32px' },
    form: { backgroundColor: '#1e1e1e', border: '1px solid #333', borderRadius: '8px', padding: '32px' },
    sectionHeader: { fontSize: '16px', fontWeight: 'bold', color: '#e0e0e0', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '16px' },
    sectionDivider: { borderTop: '1px solid #2a2a2a', margin: '28px 0 24px' },
    field: { marginBottom: '20px' },
    label: { display: 'block', fontSize: '13px', color: '#aaa', marginBottom: '7px', textTransform: 'uppercase', letterSpacing: '0.5px' },
    input: { backgroundColor: '#2c2c2c', border: '1px solid #444', borderRadius: '4px', padding: '10px 14px', fontSize: '15px', color: '#e0e0e0', width: '100%', boxSizing: 'border-box' },
    twoCol: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
    dropdown: { position: 'absolute', top: '100%', left: 0, right: 0, backgroundColor: '#2c2c2c', border: '1px solid #555', borderRadius: '4px', zIndex: 100, maxHeight: '200px', overflowY: 'auto' },
    dropdownItem: { padding: '10px 14px', cursor: 'pointer', fontSize: '14px', color: '#e0e0e0', borderBottom: '1px solid #3a3a3a' },
    selectedBadge: { marginTop: '8px', fontSize: '13px', color: '#66bb6a' },
    namePreview: { backgroundColor: '#252525', border: '1px solid #3a3a3a', borderRadius: '6px', padding: '14px 16px', fontSize: '15px', color: '#bb86fc', fontWeight: '600', marginBottom: '8px' },
    submitButton: { backgroundColor: '#bb86fc', color: '#121212', border: 'none', borderRadius: '4px', padding: '12px 24px', fontSize: '15px', fontWeight: 'bold', cursor: 'pointer', width: '100%', marginTop: '8px' },
    errorBox: { backgroundColor: '#2c1a1e', border: '1px solid #cf6679', borderRadius: '4px', padding: '12px 16px', color: '#cf6679', marginBottom: '24px', fontSize: '14px' },
    dupBox: { backgroundColor: '#1e1a0e', border: '1px solid #f0a500', borderRadius: '4px', padding: '14px 16px', color: '#f0c060', marginBottom: '24px', fontSize: '14px', lineHeight: '1.5' },
    dupLink: { color: '#bb86fc', textDecoration: 'none', fontWeight: 'bold' },
};

// ── Mobile style overrides (Spec: SHARC_Mobile_Responsive_Spec_Phase2_v1.0, §8.7) ──
// Kept as a separate object from `styles`, merged in at render time only when
// useIsMobile() is true, so a future migration to CSS @media rules can lift
// this object directly into a breakpoint block without re-deriving it.
const Smobile = {
    twoCol: {
        gridTemplateColumns: '1fr',
    },
};

export default AddProject;
